import React, { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall } from 'lucide-react';
import { getMyTasks, completeActivity } from '../../api/activities';
import { formatRelative } from '../../utils/format';
import LoadingState from '../LoadingState/LoadingState';
import { useI18n } from '../../i18n/I18nContext';
import './MyTasksWidget.css';

const SECTIONS = ['overdue', 'today', 'upcoming'];

/**
 * Current user's open tasks (overdue / today / upcoming), with inline
 * complete buttons. Self-contained card — safe to drop into any dashboard
 * or side rail. If the activities API 404s or errors, it quietly renders
 * nothing rather than showing a broken widget.
 *
 *   <MyTasksWidget />
 */
function MyTasksWidget() {
  const { t } = useI18n();
  const titleId = useId();
  const [buckets, setBuckets] = useState({ overdue: [], today: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyTasks();
      setBuckets({
        overdue: Array.isArray(res?.overdue) ? res.overdue : [],
        today: Array.isArray(res?.today) ? res.today : [],
        upcoming: Array.isArray(res?.upcoming) ? res.upcoming : [],
      });
      setAvailable(true);
    } catch {
      // Defensive: the activities module may not be deployed yet, or the
      // caller lacks access — hide the widget entirely rather than error.
      setAvailable(false);
      setBuckets({ overdue: [], today: [], upcoming: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleComplete = async (task) => {
    if (!task?.id) return;
    setBusyId(task.id);
    try {
      await completeActivity(task.id);
      await fetchTasks();
    } catch {
      // Non-fatal
    } finally {
      setBusyId(null);
    }
  };

  if (!loading && !available) return null;

  const total = buckets.overdue.length + buckets.today.length + buckets.upcoming.length;

  return (
    <section className="section-card mtw-widget" aria-labelledby={titleId} aria-busy={loading}>
      <div className="section-head mtw-head">
        <h2 className="section-head__title" id={titleId}>{t('activities.myTasks.title')}</h2>
        {total > 0 && <span className="ui-count ui-count--brand">{total}</span>}
      </div>

      {loading ? (
        <LoadingState skeleton="list" rows={3} compact />
      ) : total === 0 ? (
        <p className="mtw-empty">{t('activities.myTasks.empty')}</p>
      ) : (
        <div className="mtw-sections">
          {SECTIONS.map((key) => (
            buckets[key].length > 0 && (
              <div className="mtw-section" key={key}>
                <span className={`mtw-section-title mtw-${key}`}>{t(`activities.myTasks.${key}`)}</span>
                <ul className="mtw-list">
                  {buckets[key].map((task, i) => (
                    <li key={task?.id || i} className="mtw-item">
                      {task?.leadId ? (
                        // A planned call to a lead is done by logging the call on the lead's page
                        // (that also plans the next one), never by ticking it off here.
                        <Link
                          to={`/leads/${task.leadId}`}
                          className="mtw-check mtw-check--lead"
                          aria-label={`${t('leads.openLead')}: ${task?.subject || ''}`}
                          title={t('leads.openLead')}
                        >
                          <PhoneCall size={12} aria-hidden="true" />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="mtw-check"
                          disabled={busyId === task?.id}
                          onClick={() => handleComplete(task)}
                          aria-label={t('activities.myTasks.completeAction')}
                          title={t('activities.myTasks.completeAction')}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </button>
                      )}
                      <span className="mtw-item-body">
                        <span className="mtw-item-subject">{task?.subject || t('activities.common.untitled')}</span>
                        {task?.dueAt && <span className="mtw-item-due">{formatRelative(task.dueAt)}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ))}
        </div>
      )}
    </section>
  );
}

export default MyTasksWidget;
